/* oxlint-disable no-console */
import debounce from 'lodash-es/debounce'
import { useRef } from 'react'
import type { ResolvedIntlConfig } from 'react-intl'

/**
 * The default error logging is extremely noisy.
 * Tone it down a little for certain, commonly missing, translations.
 * See https://github.com/formatjs/formatjs/issues/465 for discussion.
 *
 * Even just turning them into warnings is quite noisy,
 * so this hook buffers them before logging them in a hidden group.
 */
export const useIntlErrorHandler = () => {
  const buffer = useRef<Set<string>>(new Set())

  const debouncedLog = useRef(
    debounce(() => {
      console.groupCollapsed(`Missing ${buffer.current.size} enum translations`)
      buffer.current.forEach((key) => console.warn(key))
      console.groupEnd()
      buffer.current.clear()
    }, 500),
  )

  const handler = useRef<ResolvedIntlConfig['onError']>((error) => {
    if (import.meta.env.MODE !== 'production') {
      if (
        error.code === 'MISSING_TRANSLATION' &&
        error.descriptor?.id?.match(/(^|\.)enum\./)
      ) {
        buffer.current.add(error.descriptor.id)
        debouncedLog.current()
      } else {
        console.error(error.message)
      }
    }
  })

  return handler.current
}
